// Procedural song descriptors consumed by MusicEngine in synth.ts.
//
// A Song is pure data: scale, chord progression, bass/lead/drum step
// patterns. The engine reads it on a 16-step-per-bar grid and schedules
// pad chords + per-step transients via the AudioContext clock.
//
// Step semantics:
//   - Each .steps array has 16 entries (16th notes per bar).
//   - A number is a scale degree RELATIVE to the current chord root
//     (so 0 = chord root, 2 = chord third, 4 = chord fifth, etc.). This
//     way a single pattern automatically follows the chord progression.
//   - null = rest (don't trigger a note on that step).
//
// Pad voicing semantics:
//   - 'root5'   → root + perfect fifth   (open, ambient)
//   - 'triad'   → root + third + fifth   (full chord)
//   - 'sus2'    → root + 2nd + fifth     (suspended, dreamy)
//   - 'cluster' → root + b2 + fifth      (dissonant — sudden death)
//
// All chords are built diatonically from the song's scale, so a major
// scale produces major triads on I/IV/V and minor on ii/iii/vi, etc.

export type SongId = 'plaza' | 'classic' | 'tdm' | 'pve' | 'race';
export type Voicing = 'root5' | 'triad' | 'sus2' | 'cluster';
export type StepNote = number | null;

export interface Song {
  id: SongId;
  bpm: number;
  rootHz: number;                 // root pitch in Hz (e.g. A2 = 110)
  scale: number[];                // semitone offsets from root
  progression: number[];          // chord roots as scale degrees per bar slot
  barsPerChord: number;           // how many bars each chord is held
  pad: {
    voicing: Voicing;
    gain: number;                 // 0..0.1 typical
    filterHz: number;             // baseline lowpass cutoff
    wave: OscillatorType;
  };
  bass: {
    steps: StepNote[];            // 16 entries
    wave: OscillatorType;
    gain: number;                 // 0..0.4
    octave: number;               // octave offset from rootHz (-1 = sub, 0 = root, 1 = up)
  };
  lead: {
    steps: StepNote[];
    wave: OscillatorType;
    gain: number;
    octave: number;
  };
  drums: {
    kick:  number[];              // 16 entries 0|1
    snare: number[];
    hat:   number[];
    hatVelMin: number;            // intensity floor below which hats are skipped
    snareVelMin: number;          // intensity floor for snare (PvE keeps quiet early)
  };
  swing?: number;                 // 0..0.3 swing on 8ths
}

// ---- Helpers ----

const semitoneToHz = (rootHz: number, semis: number) => rootHz * Math.pow(2, semis / 12);

function scaleSemitones(song: Song, degree: number): number {
  const len = song.scale.length;
  const wrappedDegree = ((degree % len) + len) % len;
  const octaveShift = Math.floor(degree / len) * 12;
  return song.scale[wrappedDegree] + octaveShift;
}

// Resolve a (chord-root, scale-degree-within-chord) pair to Hz, with
// optional octave offset. Used by bass / lead schedulers.
export function degreeToHz(song: Song, chordRoot: number, degree: number, octaveOffset = 0): number {
  const totalSemis = scaleSemitones(song, chordRoot + degree) + octaveOffset * 12;
  return semitoneToHz(song.rootHz, totalSemis);
}

// Build a chord voicing on the current chord root. Returns 2–3 frequencies.
export function chordToHz(song: Song, chordRoot: number, voicing: Voicing): number[] {
  const r = chordRoot;
  switch (voicing) {
    case 'root5':
      return [degreeToHz(song, r, 0), degreeToHz(song, r, 4)];
    case 'triad':
      return [degreeToHz(song, r, 0), degreeToHz(song, r, 2), degreeToHz(song, r, 4)];
    case 'sus2':
      return [degreeToHz(song, r, 0), degreeToHz(song, r, 1), degreeToHz(song, r, 4)];
    case 'cluster': {
      const rootSemis = scaleSemitones(song, r);
      return [
        semitoneToHz(song.rootHz, rootSemis),
        semitoneToHz(song.rootHz, rootSemis + 1),       // b2 cluster note
        degreeToHz(song, r, 4),
      ];
    }
  }
}

// Common scale dictionaries.
const MAJOR     = [0, 2, 4, 5, 7, 9, 11];   // Ionian
const NAT_MINOR = [0, 2, 3, 5, 7, 8, 10];   // Aeolian
const PHRYGIAN  = [0, 1, 3, 5, 7, 8, 10];

// ---- The 5 songs ----

export const SONGS: Record<SongId, Song> = {
  // Lobby plaza / matchmaker — warm, inviting, no urgency.
  // F major, 90 BPM, Fmaj7 → Am7 → Dm7 → C  (I-iii-vi-V style)
  plaza: {
    id: 'plaza',
    bpm: 90,
    rootHz: 87.31,                // F2
    scale: MAJOR,
    progression: [0, 2, 5, 4],    // F, Am, Dm, C
    barsPerChord: 1,
    pad: { voicing: 'triad', gain: 0.06, filterHz: 1200, wave: 'sine' },
    bass: {
      // Root on 1 + 9 (half-bar pulse), light walking note on 13.
      steps: [ 0,null,null,null, null,null,null,null,
               0,null,null,null, 2,null,null,null ],
      wave: 'triangle',
      gain: 0.18,
      octave: 0,
    },
    lead: {
      // Sparse triangle arpeggio: root, fifth, third, octave.
      steps: [ 0,null,null,null, 4,null,null,null,
               2,null,null,null, 7,null,null,null ],
      wave: 'triangle',
      gain: 0.07,
      octave: 1,
    },
    drums: {
      kick:  [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0],
      snare: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0],
      hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
      hatVelMin: 0.0,
      snareVelMin: 1.1,           // never play snare in plaza
    },
    swing: 0.05,
  },

  // Classic mode — bouncy J-pop / city-pop crossover, bright + driving.
  // A minor, 110 BPM, Am → F → C → G  (vi-IV-I-V — the pop progression)
  classic: {
    id: 'classic',
    bpm: 110,
    rootHz: 110,                  // A2
    scale: NAT_MINOR,
    progression: [0, 5, 2, 6],    // Am, F, C, G  (degrees in A natural minor)
    barsPerChord: 1,
    pad: { voicing: 'triad', gain: 0.045, filterHz: 1100, wave: 'sawtooth' },
    bass: {
      // 8th-note pulse alternating root/fifth — driving city-pop bass.
      steps: [ 0,null,0,null, 4,null,0,null,
               0,null,0,null, 4,null,2,null ],
      wave: 'sawtooth',
      gain: 0.22,
      octave: 0,
    },
    lead: {
      // NES-style square arp: root, third, fifth, octave + offbeat answers.
      steps: [ 0,null,2,null, 4,null,2,null,
               7,null,4,null, 2,null,4,null ],
      wave: 'square',
      gain: 0.06,
      octave: 1,
    },
    drums: {
      kick:  [1,0,0,0, 0,0,1,0, 1,0,0,0, 0,0,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,1],
      hatVelMin: 0.0,
      snareVelMin: 0.0,
    },
  },

  // TDM — aggressive, heavy, dark.
  // D minor, 124 BPM, Dm → Bb → F → C  (i-VI-III-VII), descending bass.
  tdm: {
    id: 'tdm',
    bpm: 124,
    rootHz: 73.42,                // D2
    scale: NAT_MINOR,
    progression: [0, 5, 2, 6],
    barsPerChord: 1,
    pad: { voicing: 'root5', gain: 0.05, filterHz: 900, wave: 'sawtooth' },
    bass: {
      // Driving 8ths, chromatic walk on bar 4 (handled by varying step 14).
      steps: [ 0,null,0,null, 0,null,4,null,
               0,null,0,null, 4,null,2,null ],
      wave: 'sawtooth',
      gain: 0.28,
      octave: 0,
    },
    lead: {
      // Stabby square+saw lead — short hits on offbeats.
      steps: [ null,null,4,null, null,null,7,null,
               null,null,4,null, null,null,2,null ],
      wave: 'sawtooth',
      gain: 0.07,
      octave: 1,
    },
    drums: {
      kick:  [1,0,0,1, 0,0,1,0, 1,0,0,1, 0,0,1,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,1],
      hat:   [1,1,1,1, 1,1,1,1, 1,1,1,1, 1,1,1,1],
      hatVelMin: 0.0,
      snareVelMin: 0.0,
    },
  },

  // PvE — atmospheric, ritualistic, dark phrygian.
  // E phrygian, 78 BPM, drone-y. Hats only when intensity climbs.
  pve: {
    id: 'pve',
    bpm: 78,
    rootHz: 82.41,                // E2
    scale: PHRYGIAN,
    progression: [0, 1, 0, 6],    // i-bII-i-VII — phrygian signature half-step
    barsPerChord: 1,
    pad: { voicing: 'sus2', gain: 0.07, filterHz: 700, wave: 'sawtooth' },
    bass: {
      // Sub-bass chant — root on every quarter, sparse.
      steps: [ 0,null,null,null, null,null,null,null,
               0,null,null,null, null,null,null,null ],
      wave: 'sine',
      gain: 0.32,
      octave: -1,
    },
    lead: {
      // Sparse, only at high intensity — single long bell-tone per bar.
      steps: [ null,null,null,null, null,null,null,null,
               4,null,null,null, null,null,null,null ],
      wave: 'sine',
      gain: 0.06,
      octave: 1,
    },
    drums: {
      // Taiko-like single low kick on 1.
      kick:  [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0],
      snare: [0,0,0,0, 0,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [0,0,1,0, 0,0,1,0, 0,0,1,0, 0,0,1,0],
      hatVelMin: 0.5,             // hats appear only when match heats up
      snareVelMin: 0.7,
    },
  },

  // Race — anthem, fast, bright, four-on-the-floor.
  // E major, 140 BPM, E → B → C#m → A  (I-V-vi-IV).
  race: {
    id: 'race',
    bpm: 140,
    rootHz: 82.41,                // E2
    scale: MAJOR,
    progression: [0, 4, 5, 3],
    barsPerChord: 1,
    pad: { voicing: 'triad', gain: 0.05, filterHz: 1400, wave: 'sawtooth' },
    bass: {
      // Galloping triplet-feel via 16th syncopation.
      steps: [ 0,0,null,0, 0,null,0,0, null,0,0,null, 4,null,2,null ],
      wave: 'sawtooth',
      gain: 0.24,
      octave: 0,
    },
    lead: {
      // Bright square-wave anthem hook.
      steps: [ 4,null,7,null, 9,null,7,null, 4,null,2,null, 0,null,2,null ],
      wave: 'square',
      gain: 0.07,
      octave: 1,
    },
    drums: {
      // Four-on-the-floor + open-hat offbeats.
      kick:  [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
      hat:   [0,0,1,0, 0,0,1,0, 0,0,1,0, 0,0,1,1],
      hatVelMin: 0.0,
      snareVelMin: 0.0,
    },
  },
};
